import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { ROUTES } from '@/constants/routes'
import { LoginModal } from '@/components/landing/LoginModal'
import { RegisterModal } from '@/components/landing/RegisterModal'
import { lumenBaseUrl } from '@/lib/lumenHost'

type ColorToken = 'danger' | 'beacon' | 'success' | 'community'

const FEATURES: { icon: string; color: ColorToken; title: string; desc: string }[] = [
  { icon: 'fa-triangle-exclamation', color: 'danger',    title: 'SOS Alerts',      desc: 'One tap sends your location to your entire care team' },
  { icon: 'fa-shield-halved',        color: 'danger',    title: 'Safe Zones',      desc: 'Get alerted the moment they leave a safe area' },
  { icon: 'fa-qrcode',               color: 'beacon',    title: 'QR Safety Card',  desc: 'Emergency info accessible to first responders in seconds' },
  { icon: 'fa-hospital',             color: 'beacon',    title: 'Hospital Passport', desc: 'Medical info available offline and across providers' },
  { icon: 'fa-location-dot',         color: 'beacon',    title: 'Live Location',   desc: 'Share real-time location with carers you trust' },
  { icon: 'fa-address-card',         color: 'success',   title: 'Safety Profiles', desc: 'Critical emergency info, always ready for first responders' },
  { icon: 'fa-building-user',        color: 'success',   title: 'Care Teams',      desc: 'Connect with schools, therapists & support orgs' },
  { icon: 'fa-comments',             color: 'community', title: 'Messaging',       desc: 'Stay in touch with everyone on your care team' },
  { icon: 'fa-tornado',              color: 'danger',    title: 'Hazard Watch',    desc: 'Storm, flood & earthquake alerts near you' },
  { icon: 'fa-comment-dots',         color: 'community', title: 'My Voice',        desc: 'Tap-to-talk AAC board that gives nonverbal kids a voice' },
  { icon: 'fa-users',                color: 'community', title: 'Community',       desc: 'Share tips and connect with special needs families' },
  { icon: 'fa-hand-holding-heart',   color: 'success',   title: 'Find Help',       desc: 'Discover nearby aid programs and NGO resources' },
]

const TOKEN_BG: Record<ColorToken, string> = {
  danger:    'bg-danger-50',
  beacon:    'bg-beacon-50',
  success:   'bg-success-50',
  community: 'bg-community-50',
}
const TOKEN_TEXT: Record<ColorToken, string> = {
  danger:    'text-danger-500',
  beacon:    'text-beacon-500',
  success:   'text-success-600',
  community: 'text-community-500',
}

export default function LandingView() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const devLogin = useAuthStore((s) => s.devLogin)
  const [showLoginModal, setShowLoginModal]       = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)

  useEffect(() => {
    document.title = "David's Beacon"
    return () => { document.title = "David's Beacon" }
  }, [])

  // Auto-open correct modal based on URL params
  useEffect(() => {
    const modal    = searchParams.get('modal') ?? ''
    const redirect = searchParams.get('redirect') ?? ''
    if (modal === 'register') { setShowRegisterModal(true); return }
    // Invites + org deep links from Beacon Ops (Safety tools) open login immediately.
    if (
      modal === 'login'
      || redirect.startsWith('/invite/')
      || redirect.startsWith('/org-invite/')
      || redirect.startsWith('/org')
    ) {
      setShowLoginModal(true)
    }
  }, [searchParams])

  function openRegister() { setShowLoginModal(false); setShowRegisterModal(true) }
  function openLogin()    { setShowRegisterModal(false); setShowLoginModal(true) }

  function handleDevLogin(as: 'family' | 'org') {
    devLogin(as)
    navigate(as === 'org' ? ROUTES.ORG : (searchParams.get('redirect') ?? ROUTES.HOME))
  }

  return (
    <div className="min-h-screen bg-sky-50 flex flex-col">

      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/favicon.svg" alt="David's Beacon" className="w-7 h-7" />
            <span className="font-extrabold text-slate-900 text-sm hidden sm:inline">David's Beacon</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Staff product = Lumen only (never Filament org.davidsbeacon.com). */}
            <a
              href={`${lumenBaseUrl()}/lumen/login`}
              title="Lumen staff login"
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-beacon-600 px-3 py-1.5 rounded-lg hover:bg-beacon-50 transition-colors"
            >
              Staff login
              <i className="fa-solid fa-arrow-up-right-from-square text-xs opacity-70" aria-hidden="true" />
            </a>
            <button
              onClick={() => setShowLoginModal(true)}
              className="text-sm font-semibold text-slate-700 hover:text-beacon-600 px-3 py-1.5 rounded-lg hover:bg-beacon-50 transition-colors"
            >
              Log In
            </button>
            <button
              onClick={openRegister}
              className="text-sm font-semibold bg-beacon-600 hover:bg-beacon-700 text-white px-4 py-1.5 rounded-lg transition-colors shadow-sm"
            >
              Register
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="flex flex-col items-center justify-center text-center px-6 pt-16 pb-8 lg:pt-24 lg:pb-10">
        <div className="max-w-2xl">
          <h1 className="font-extrabold text-3xl sm:text-4xl text-slate-900 leading-tight mb-4">
            Safety tools for families who need more than a group chat.
          </h1>
          <p className="text-base text-slate-500 mb-8 max-w-xl mx-auto">
            Real-time SOS, Safe Zones, Emergency ID, and care team messaging — built for families of individuals with special needs.
          </p>
          <div className="flex flex-col items-center gap-3 w-full max-w-xs mx-auto">
            <button
              onClick={openRegister}
              className="w-full bg-beacon-600 hover:bg-beacon-700 text-white font-bold px-6 py-3 rounded-xl shadow-md transition-colors text-sm"
            >
              Get started free →
            </button>
            <button
              onClick={() => setShowLoginModal(true)}
              className="w-full border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-600 hover:text-beacon-600 py-3 rounded-xl transition-colors"
            >
              Already have an account? Log in
            </button>
          </div>
          <p className="text-xs text-slate-600 font-semibold mt-4">Free for families, always · No ads · No data sold</p>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-5xl mx-auto w-full px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {FEATURES.map(({ icon, color, title, desc }) => (
            <div key={title} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
              <div className={`w-9 h-9 rounded-xl ${TOKEN_BG[color]} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <i className={`fa-solid ${icon} ${TOKEN_TEXT[color]} text-sm`} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6 mt-auto">
        <div className="max-w-5xl mx-auto px-4 flex flex-col items-center gap-2 text-xs">
          <div className="flex items-center flex-wrap justify-center font-semibold text-slate-600">
            {[
              { label: 'Terms',     route: ROUTES.TERMS },
              { label: 'Privacy',   route: ROUTES.PRIVACY_POLICY },
              { label: 'Security',  route: ROUTES.SECURITY_PRIVACY },
              { label: 'Our Story', route: ROUTES.ABOUT },
            ].map(({ label, route }, i) => (
              <span key={label} className="flex items-center">
                {i > 0 && <span className="mx-1 text-slate-300">/</span>}
                <button onClick={() => navigate(route)} className="hover:text-beacon-500 transition-colors">{label}</button>
              </span>
            ))}
          </div>
          <p className="font-medium text-slate-500">Built by a special needs family, for families like ours.</p>
        </div>
      </footer>

      {/* Dev login helper */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-4 right-4 flex gap-2 z-50">
          <button onClick={() => handleDevLogin('family')} className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg shadow">[DEV] Family</button>
          <button onClick={() => handleDevLogin('org')} className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg shadow">[DEV] Org</button>
        </div>
      )}

      {showLoginModal    && <LoginModal onClose={() => setShowLoginModal(false)} onSwitchToRegister={openRegister} />}
      {showRegisterModal && <RegisterModal onClose={() => setShowRegisterModal(false)} onSwitchToLogin={openLogin} />}
    </div>
  )
}
